import { IModalTextField } from 'interfaces/components/modalTextField';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import isURL from 'validator/es/lib/isURL';
import { formatMoney } from 'utils/helpers';
import ConfirmWarningIcon from 'assets/icons/confirm_warning.svg';
import CalculateIcon from 'assets/icons/calculate.svg';
import ColorPicker from './ColorPicker';
import { yourCompanyColor } from 'utils/constants';
import ButtonWithIcon from 'components/ButtonWithIcon';
import CustomTooltipList from 'components/TooltipList';
import { truncateToDecimal } from 'utils/helpers/truncateToDecimal';
import TextFieldLabel from './textFieldLabel';

export const HEX_COLOR_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

const ModalTextField = ({
  label,
  tooltip,
  character,
  isMoney,
  wrapWithBrackets,
  name,
  value,
  onChange,
  placeholder,
  isNumber,
  isNegative,
  isDecimal,
  isNonZero,
  isWebsite,
  isColorSelector,
  isPrefixCharacter,
  showInfoIcon,
  showCalculateBtn,
  onCalculateBtnClick,
  min,
  max,
  minWarning,
  maxWarning,
  warning,
  disabled,
  infoIcon,
  tooltipList,
  tooltipListTitle,
  labelStyle,
  containerStyle,
  inputStyle,
  height = 'h-11',
  useCommaFormat = false,
  forceNegativeSign = false
}: IModalTextField) => {
  const fakeSpan = useRef<HTMLSpanElement>(null);
  const infoRef = useRef<HTMLInputElement>(null);
  const [width, setWidth] = useState(0);
  const [isWarningActive, setIsWarningActive] = useState(false);
  const [isWarningConfirmed, setIsWarningConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [showTooltipList, setShowTooltipList] = useState(false);

  const handleTooltipToggle = () => {
    setShowTooltipList(prev => !prev);
  };

  const onValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = wrapWithBrackets
      ? event.target.value.replace(/[()$\s]/g, '')
      : event.target.value;

    if (isColorSelector) {
      const colorValue = event.target.value;
      const valueToValidate = colorValue.startsWith('#')
        ? colorValue
        : `#${colorValue}`;

      if (HEX_COLOR_REGEX.test(valueToValidate)) {
        setError('');
        onChange(valueToValidate);
      } else {
        setError('Please enter a valid hex color code');
        onChange(colorValue);
      }
      return;
    }

    if (isNumber || isMoney) {
      let currentString = rawValue.replaceAll(/,/g, '');
      if (currentString.length > 1 && isNegative) {
        currentString =
          currentString.charAt(0) === '0'
            ? currentString.substring(1)
            : currentString;
      }
      let currentNumber;
      if (isDecimal && currentString.includes('.')) {
        const checkDecimal = currentString.split('.');
        if (checkDecimal[1].length > 1) {
          return;
        }
      }

      // Prevent decimal input for percentage fields
      if (character === '%' && currentString.includes('.')) {
        currentString = truncateToDecimal(Number(currentString)).toString();
      }

      if (isNegative) {
        if (!isDecimal) {
          currentString =
            currentString.charAt(currentString.length - 1) === '.'
              ? currentString.slice(0, -1)
              : currentString;
        }
        currentNumber = Number(
          currentString === '-' ? currentString + '1' : currentString
        );
      } else {
        if (currentString.includes('-')) {
          return;
        }

        if (isDecimal) {
          currentNumber = Number(
            currentString.charAt(currentString.length - 1) === '.'
              ? currentString + '0'
              : currentString
          );
        } else {
          currentString =
            currentString.charAt(currentString.length - 1) === '.'
              ? currentString.slice(0, -1)
              : currentString;
          currentNumber = Number(currentString);
        }
      }

      if (!isNaN(currentNumber)) {
        const isBelowMin = min !== undefined && currentNumber < min;
        const isAboveMax = max !== undefined && currentNumber > max;
        if (!(isBelowMin || isAboveMax)) {
          if (!(isNonZero && currentNumber === 0)) {
            onChange?.(currentString);
          } else {
            onChange('');
          }
        } else {
          return;
        }
        if (
          ((minWarning !== undefined && currentNumber < minWarning) ||
            (maxWarning !== undefined && currentNumber > maxWarning)) &&
          currentString
        ) {
          setIsWarningActive(true);
        } else {
          setIsWarningActive(false);
        }
      }
    } else {
      if (
        isWebsite &&
        !isURL(event.target.value, { require_protocol: false }) &&
        !!event.target.value
      ) {
        setIsWarningActive(true);
      } else {
        setIsWarningActive(false);
      }
      onChange?.(rawValue);
    }
  };

  const onBlur = () => {
    if (isColorSelector) {
      const colorValue = String(value);
      const valueToValidate = colorValue.startsWith('#')
        ? colorValue
        : `#${colorValue}`;

      if (!HEX_COLOR_REGEX.test(valueToValidate)) {
        onChange(yourCompanyColor);
      }
      return;
    }

    if (isMoney || isNumber) {
      const currentNumber = Number(value);
      if (isNaN(currentNumber)) onChange('');
      if (isDecimal) {
        const currentString = String(value);
        if (currentString.charAt(currentString.length - 1) === '.') {
          onChange(currentString.slice(0, -1));
        }
      }
    }
  };

  const onWarningConfirmed = () => {
    setIsWarningConfirmed(true);
  };

  useEffect(() => {
    if (fakeSpan && fakeSpan.current) {
      setWidth(fakeSpan.current.offsetWidth);
    }
  }, [value]);

  const handleColorPickerChange = (newColor: string) => {
    if (HEX_COLOR_REGEX.test(newColor)) {
      setError('');
    }
    onChange(newColor);
  };

  const formatDisplayValue = () => {
    if (!value) {
      if (forceNegativeSign) return '-';
      return value;
    }

    // Handle money fields
    if (isMoney) {
      const formattedValue = useCommaFormat
        ? Number(String(value).replaceAll(/,/g, '')).toLocaleString()
        : String(value).replaceAll(/,/g, '');

      return wrapWithBrackets
        ? `(${character} ${formattedValue})`
        : formattedValue;
    }

    // Handle number fields with comma formatting
    if (isNumber && useCommaFormat) {
      if (isNegative && value === '-') {
        return value;
      }
      return Number(String(value).replaceAll(/,/g, '')).toLocaleString();
    }

    if (wrapWithBrackets) {
      return `(${character} ${value})`;
    }

    return value;
  };

  return (
    <div
      className={`w-full flex flex-col items-start justify-end gap-2 ${containerStyle}`}
    >
      <TextFieldLabel
        label={label}
        tooltip={tooltip}
        name={name}
        labelStyle={labelStyle}
      />
      <div className='flex flex-row justify-start items-center gap-2 relative w-full'>
        <div
          className={`border ${
            error
              ? 'border-red-500'
              : isWarningActive && !isWarningConfirmed
              ? 'border-black'
              : 'border-gray-400'
          } dark:border-gray-800 border-solid w-full ${height} pl-4 rounded-lg flex flex-row items-center justify-between ${
            disabled
              ? 'bg-gray-200 dark:bg-gray-900 cursor-not-allowed'
              : 'dark:bg-black'
          }`}
        >
          <div
            className={`flex items-center gap-1 h-full relative ${
              !isMoney && !showCalculateBtn && 'w-full'
            }`}
          >
            {isColorSelector && (
              <ColorPicker
                color={value as string}
                onChange={handleColorPickerChange}
              />
            )}
            <span
              ref={fakeSpan}
              className='absolute text-lg opacity-0 -z-[100]'
            >
              {value}
            </span>
            <input
              className={`min-w-[1px] w-full h-full dark:bg-black focus:outline-none placeholder:text-gray-500 relative text-lg placeholder:text-lg focus:text-lg disabled:cursor-not-allowed ${
                isPrefixCharacter && !wrapWithBrackets && 'pl-3'
              } ${inputStyle}`}
              type='text'
              name={name}
              value={formatDisplayValue()}
              onChange={onValueChange}
              onBlur={onBlur}
              placeholder={placeholder}
              maxLength={isMoney || isNumber ? 20 : undefined}
              disabled={disabled}
            />
            {!!character && !wrapWithBrackets && (
              <p
                className='text-lg font-light text-gray-500 absolute'
                style={{
                  left: `${isPrefixCharacter ? 0 : !!width ? width + 5 : 12}px`
                }}
              >
                {character}
              </p>
            )}
          </div>
          <div className='flex flex-row items-center' ref={infoRef}>
            {showInfoIcon && (
              <img
                src={infoIcon}
                alt='Info Icon'
                className='cursor-pointer pr-[6px]'
                onMouseEnter={handleTooltipToggle}
                onMouseLeave={handleTooltipToggle}
              />
            )}
            {showCalculateBtn && (
              <ButtonWithIcon
                id='calculate_button'
                prefixIcon={CalculateIcon}
                prefixIconAlt='Calculate Icon'
                label='Calculate'
                className='mr-2 bg-green-50 dark:text-black rounded-[4px] py-[6px] px-2 flex flex-row items-center cursor-pointer hover:bg-green-200 transition-all duration-300'
                textStyle='text-[14px] leading-[16px] font-semibold text-green-700'
                onClick={onCalculateBtnClick}
                disabled={disabled}
              />
            )}
          </div>
          {isMoney && (
            <div className='w-[100px] h-full flex flex-row items-center justify-center px-4 py-2 bg-gray-200 dark:bg-gray-900 border-l border-solid border-gray-400 dark:border-gray-625 rounded-r-lg pointer-events-none'>
              <p className='text-lg font-light text-nowrap'>
                {character + ' ' + (value ? formatMoney(Number(value), 2) : 0)}
              </p>
            </div>
          )}
        </div>
        {showTooltipList &&
          (tooltipListTitle || (tooltipList && tooltipList.length > 0)) && (
            <CustomTooltipList
              tooltipList={tooltipList}
              tooltipListTitle={tooltipListTitle}
              triggerElement={infoRef.current}
            />
          )}
      </div>
      {error && (
        <div className='flex flex-row items-start gap-2'>
          <p className='text-sm font-light'>{error}</p>
        </div>
      )}
      {isWarningActive && warning && !isWarningConfirmed && (
        <div className='flex flex-row items-start gap-2'>
          <p className='text-sm font-light'>
            {isWebsite
              ? warning
              : isPrefixCharacter
              ? warning +
                ' ' +
                character +
                (useCommaFormat
                  ? Number(String(value).replaceAll(/,/g, '')).toLocaleString()
                  : value) +
                '?'
              : warning +
                ' ' +
                (useCommaFormat
                  ? Number(String(value).replaceAll(/,/g, '')).toLocaleString()
                  : value) +
                character +
                '?'}
          </p>
          <img
            src={ConfirmWarningIcon}
            alt=''
            className='cursor-pointer'
            onClick={onWarningConfirmed}
          />
        </div>
      )}
    </div>
  );
};

export default ModalTextField;
